/**
 * Marketplace export kit — Facebook copy block and the photo contact sheet.
 *
 * Facebook Marketplace has no public listing API, so the FB path is a "copy
 * kit": a paste-ready text block plus a downloadable HTML contact sheet of
 * the lot photos (auction images are cross-origin, so we can't zip them
 * client-side — the sheet renders them for easy right-click saving).
 *
 * @module portal/modules/marketplaceExport
 */

import { buildFacebookCopy, buildListingBasics, type ListingBasics } from '@lotlingo/shared';
import type { Lot } from '../types';
import { getLotMarketComps } from './compsPanel';
import { parseCurrencyValue } from './formatting';

/** Build the marketplace-neutral listing basics for a portal lot. */
export function lotListingBasics(lot: Lot): ListingBasics {
  const comps = getLotMarketComps(lot);
  return buildListingBasics({
    title: lot.title,
    description: typeof lot.notes === 'string' ? lot.notes : lot.description,
    lowEstimate: parseCurrencyValue(lot.lowSellValue ?? lot.lowSellValueRaw),
    highEstimate: parseCurrencyValue(lot.highSellValue ?? lot.highSellValueRaw),
    compsMedian: typeof comps?.trimmedMedian === 'number' ? comps.trimmedMedian : null,
  });
}

/** Paste-ready Facebook Marketplace text for a lot. */
export function buildLotFacebookCopy(lot: Lot): string {
  return buildFacebookCopy(lotListingBasics(lot));
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function safeImageUrls(lot: Lot): string[] {
  const images = Array.isArray(lot.images) ? lot.images : [];
  return images.filter((url): url is string => typeof url === 'string' && /^https:\/\//i.test(url));
}

/**
 * Standalone HTML contact sheet of the lot's photos (download + open in a
 * tab; right-click any photo to save it for the marketplace upload).
 */
export function buildPhotoSheetHtml(lot: Lot): string {
  const basics = lotListingBasics(lot);
  const title = escapeHtml(basics.title || 'Lot photos');
  const images = safeImageUrls(lot)
    .map(
      url =>
        `<figure><img src="${escapeHtml(url)}" alt="${title}" loading="lazy">` +
        `<figcaption><a href="${escapeHtml(url)}" target="_blank" rel="noopener">open original</a></figcaption></figure>`
    )
    .join('\n');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${title} — photos</title>
<style>body{font-family:system-ui,sans-serif;margin:1.5rem;background:#fafafa;color:#222}
h1{font-size:1.1rem}p{color:#666;font-size:.9rem}
main{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:1rem}
figure{margin:0;background:#fff;border:1px solid #e2e2e2;border-radius:8px;padding:.5rem}
img{width:100%;height:auto;border-radius:4px}figcaption{font-size:.8rem;margin-top:.25rem}</style>
</head><body>
<h1>${title}</h1>
<p>Right-click a photo and choose “Save image as…” to download it for your listing.</p>
<main>${images || '<p>No photos were captured for this lot.</p>'}</main>
</body></html>`;
}

/** Trigger a browser download of arbitrary text content. */
export function downloadTextFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Filesystem-safe slug for download filenames. */
export function lotFileSlug(lot: Lot): string {
  const base = (lot.title || lot.id || 'lot')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return base || 'lot';
}

/** Copy text to the clipboard, with a hidden-textarea fallback. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      // CSSOM assignment (not an inline style attribute) — allowed under the
      // portal CSP.
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      textarea.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
